import { useEffect, useRef, useState } from "react";
import { Package, Video, Loader2, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * BossLootChestModal — appears immediately after a boss defeat. The player
 * gets ONE opt-in choice to watch a short ad in exchange for a random rare
 * item drop. The window is 5 minutes (backend enforces via last_boss_kill_at).
 *
 * Fires: POST /api/ads/reward/loot-chest
 */
export default function BossLootChestModal({ open, onClose, onClaimed }) {
  const [phase, setPhase] = useState("idle"); // idle | playing | granting | claimed
  const [countdown, setCountdown] = useState(5);
  const [reward, setReward] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset state each time the modal opens fresh
      setPhase("idle"); setCountdown(5); setReward(null);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [open]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  if (!open) return null;

  const startAd = () => {
    setPhase("playing");
    setCountdown(5);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          grant();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const grant = async () => {
    setPhase("granting");
    try {
      const ad_token = `mock_chest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const res = await api.post("/ads/reward/loot-chest", { ad_token, placement: "loot_chest" });
      if (res?.ok) {
        setReward(res);
        setPhase("claimed");
        toast.success(`Loot chest opened — ${res.item_name}`);
        onClaimed?.(res);
      } else {
        throw new Error("Chest failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "The chest is sealed.";
      toast.error(detail);
      setPhase("idle");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      data-testid="loot-chest-modal"
    >
      <div
        className="relative w-full max-w-md mx-4 panel p-6 border-2"
        style={{ borderColor: "#d4af37", boxShadow: "0 0 60px rgba(212,175,55,0.30)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#f0e6d2] opacity-60 hover:opacity-100 transition-opacity"
          data-testid="loot-chest-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Header: gilded chest icon with ember glow */}
        <div className="flex flex-col items-center text-center">
          <div
            className="relative w-20 h-20 flex items-center justify-center mb-3"
            style={{
              background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)",
              animation: "chest-glow 2.4s ease-in-out infinite",
            }}
          >
            <Package size={44} className="gold-text" />
            <Sparkles size={12} className="absolute top-0 right-2 gold-text" style={{ animation: "chest-sparkle 1.6s ease-in-out infinite" }} />
            <Sparkles size={10} className="absolute bottom-2 left-1 gold-text opacity-70" style={{ animation: "chest-sparkle 1.6s ease-in-out infinite 0.6s" }} />
          </div>
          <div className="text-[10px] tracking-[0.4em] gold-text">— BOSS DEFEATED —</div>
          <h3 className="font-heading text-3xl gold-text mt-1">A Gilded Chest.</h3>
          <p className="text-sm ash-text mt-2 italic max-w-xs">
            The fallen tyrant's hoard is unclaimed. Open it now, and something rare will follow you into the dark.
          </p>
        </div>

        {/* CTA area */}
        {phase === "idle" && (
          <div className="mt-6 space-y-3">
            <button
              onClick={startAd}
              data-testid="loot-chest-open-btn"
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Video size={14} /> OPEN THE CHEST · WATCH AD
            </button>
            <button
              onClick={onClose}
              data-testid="loot-chest-leave-btn"
              className="w-full text-[10px] tracking-[0.35em] ash-text hover:text-[#f0e6d2] transition-colors py-2"
            >
              LEAVE IT — CARRY ON
            </button>
          </div>
        )}

        {phase === "playing" && (
          <div className="mt-6 panel p-4 flex items-center justify-center gap-3" data-testid="loot-chest-playing">
            <Video size={16} className="gold-text" />
            <span className="text-[10px] tracking-[0.3em] ash-text">DEV AD ·</span>
            <span className="font-heading text-xl gold-text">{countdown}s</span>
          </div>
        )}

        {phase === "granting" && (
          <div className="mt-6 panel p-4 flex items-center justify-center gap-2" data-testid="loot-chest-granting">
            <Loader2 size={14} className="animate-spin gold-text" />
            <span className="text-[10px] tracking-[0.3em] ash-text">UNSEALING...</span>
          </div>
        )}

        {phase === "claimed" && reward && (
          <div className="mt-6 space-y-3" data-testid="loot-chest-claimed">
            <div
              className="panel p-4 text-center border-2"
              style={{ borderColor: "#d4af37", background: "linear-gradient(180deg, rgba(212,175,55,0.10), transparent)" }}
            >
              <div className="text-[10px] tracking-[0.4em] gold-text">— CHEST YIELDS —</div>
              <div className="font-heading text-2xl text-[#f0e6d2] mt-2">{reward.item_name}</div>
              <div className="text-[10px] ash-text tracking-widest mt-1">Added to your pack.</div>
            </div>
            <button
              onClick={onClose}
              data-testid="loot-chest-continue-btn"
              className="btn-primary w-full"
            >
              CARRY ON
            </button>
          </div>
        )}

        <style>{`
          @keyframes chest-glow {
            0%,100% { transform: scale(1); filter: brightness(1); }
            50%     { transform: scale(1.08); filter: brightness(1.25); }
          }
          @keyframes chest-sparkle {
            0%,100% { opacity: 0.3; transform: translateY(0) scale(0.9); }
            50%     { opacity: 1;   transform: translateY(-2px) scale(1.1); }
          }
        `}</style>
      </div>
    </div>
  );
}
